"use client";

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useHeaderDesign } from "../layouts/HeaderDesignContext";
import {
  HeaderMotionProvider,
  headerMotion,
} from "@/components/motion/header-motion";
import {
  headerMenuPresentation,
  type MenuPresentation,
} from "@/utils/header-menu-presentation";
import type { ResolvedNavigation } from "@/utils/header-navigation";
import type { ResolvedHeaderAction } from "@/utils/header-actions";
import {
  useResponsiveWidth,
  navigationBreakpoints,
  type NavigationBreakpoint,
} from "@/hooks/useMinWidth";
import { forDevice } from "@/utils/header-navigation";
import NavigationBar from "./NavigationBar";
import NavigationPanel from "./NavigationPanel";
import NavigationToggle from "./NavigationToggle";
import HeaderActions from "./HeaderActions";
import { NavigationFormatting } from "./NavigationFormatting";
import MenuActions from "./MenuActions";
import type { HeaderActionIcons } from "./HeaderActionItem";

/**
 * The header row used once `layout.header.navigation` or `layout.header.menu` is present: logo,
 * desktop bar, action area and menu button, plus the panel. The header renders the mobile bottom
 * bar itself, outside its own element, because a transformed header would trap a fixed bar.
 * `legacyActions` is the type's own action area, used while `layout.header.actions` is absent.
 */
export default function HeaderMenu({
  navigation,
  actions,
  legacyActions,
  logo,
  panelLogo,
  logoPosition = "start",
  layout = "row",
  rowClassName,
  rowStyle,
  barClassName,
  barAlign = "end",
  hoverMarker,
  bp,
  appearance,
  anchor,
  onSearch,
  icons,
  panelExtras,
  open: controlledOpen,
  onOpenChange,
  presentation: suppliedPresentation,
  inheritedTextClassName,
  schemeSwitch,
  lookSystem = false,
}: {
  navigation: ResolvedNavigation;
  actions?: ResolvedHeaderAction[];
  legacyActions?: React.ReactNode;
  logo: React.ReactNode;
  panelLogo?: React.ReactNode;
  logoPosition?: "start" | "center";
  /** "rows": logo and actions on top, the bar on its own line (Klassisk with actions). */
  layout?: "row" | "rows";
  rowClassName?: string;
  rowStyle?: React.CSSProperties;
  barClassName?: string;
  barAlign?: "start" | "end" | "between";
  hoverMarker?: "slashes";
  bp: NavigationBreakpoint;
  appearance: "default" | "floating" | "classic";
  anchor: React.RefObject<HTMLElement | null>;
  onSearch?: () => void;
  icons?: HeaderActionIcons;
  panelExtras?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  presentation?: MenuPresentation;
  inheritedTextClassName?: string;
  /** The look system's light/dark switch, beside the menu button. */
  schemeSwitch?: React.ReactNode;
  /** The menu and its panel animate from motion/react on the look system, from framer-motion on old websites. */
  lookSystem?: boolean;
}) {
  const { AnimatePresence } = headerMotion(lookSystem);
  const panelId = useId();
  const headerDesign = useHeaderDesign();
  const [probeHost, setProbeHost] = useState<HTMLElement | null>(null);
  useEffect(() => setProbeHost(document.body), []);
  const toggleRef = useRef<HTMLButtonElement>(null);
  const [ownOpen, setOwnOpen] = useState(false);
  const open = controlledOpen ?? ownOpen;
  const setOpen = useCallback(
    (value: boolean) => {
      setOwnOpen(value);
      onOpenChange?.(value);
    },
    [onOpenChange],
  );
  const presentation =
    suppliedPresentation ??
    headerMenuPresentation({ type: navigation.type, menu: navigation.options });
  const probe = useRef<HTMLDivElement>(null);
  const [inheritedStyle, setInheritedStyle] = useState<Record<string, string>>(
    {},
  );
  const measureTypography = useCallback(() => {
    const node = probe.current?.querySelector<HTMLElement>(
      "[data-menu-inherited-text]",
    );
    if (!node) return;
    const style = getComputedStyle(node);
    const next = {
      "--re-menu-font-size": style.fontSize,
      "--re-menu-line-height":
        Number.isFinite(parseFloat(style.lineHeight)) &&
        parseFloat(style.fontSize) > 0
          ? String(parseFloat(style.lineHeight) / parseFloat(style.fontSize))
          : style.lineHeight,
      "--re-menu-font-family": style.fontFamily,
      "--re-menu-font-weight": style.fontWeight,
      "--re-menu-font-style": style.fontStyle,
      "--re-menu-letter-spacing": style.letterSpacing,
      "--re-menu-text-transform": style.textTransform,
    };
    setInheritedStyle((previous) =>
      JSON.stringify(previous) === JSON.stringify(next) ? previous : next,
    );
  }, []);
  useLayoutEffect(measureTypography);
  useEffect(() => {
    let mounted = true;
    window.addEventListener("resize", measureTypography);
    document.fonts?.addEventListener("loadingdone", measureTypography);
    void document.fonts?.ready.then(() => {
      if (mounted) measureTypography();
    });
    return () => {
      mounted = false;
      window.removeEventListener("resize", measureTypography);
      document.fonts?.removeEventListener("loadingdone", measureTypography);
    };
  }, [measureTypography]);
  const [overflowKeys, setOverflowKeys] = useState<string[]>([]);
  const onOverflowChange = useCallback((keys: string[]) => {
    setOverflowKeys((previous) =>
      previous.length === keys.length &&
      previous.every((key, index) => key === keys[index])
        ? previous
        : keys,
    );
  }, []);
  const close = useCallback(() => setOpen(false), [setOpen]);
  const { options } = navigation;
  const desktop = useResponsiveWidth(navigationBreakpoints[bp]);
  const barItems =
    desktop || !options.overflow
      ? navigation.bar
      : forDevice(navigation.bar, "d");
  const visibleKeys = new Set(
    barItems
      .filter((item) => !overflowKeys.includes(item.key))
      .map((item) => item.key),
  );
  const hoverEffect =
    options.hoverEffect ?? (appearance === "default" ? "chevron" : undefined);
  const overflowed = navigation.bar.filter((item) =>
    overflowKeys.includes(item.key),
  );
  const desktopItems = [...overflowed, ...navigation.panel];
  const mobileItems = options.overflow
    ? forDevice(navigation.items, "d").filter(
        (item) => !visibleKeys.has(item.key),
      )
    : navigation.mobile;
  const focusToggle = useCallback(() => {
    requestAnimationFrame(() => toggleRef.current?.focus());
  }, []);
  const menuActions = actions?.filter((action) => action.mobile === "menu");
  const desktopEmpty = !desktopItems.length && !panelExtras;
  const mobileEmpty =
    !mobileItems.length && !menuActions?.length && !panelExtras;
  const search = onSearch
    ? () => {
        setOpen(false);
        onSearch();
      }
    : undefined;

  const bar = barItems.length ? (
    <NavigationBar
      items={barItems}
      submenus={options.submenus}
      bp={bp}
      overflow={options.overflow}
      onOverflowChange={onOverflowChange}
      className={barClassName}
      align={
        options.barAlignment === "left"
          ? "start"
          : options.barAlignment === "right"
            ? "end"
            : logoPosition === "center"
              ? "start"
              : barAlign
      }
      hoverEffect={hoverEffect}
      hoverMarker={hoverMarker}
      appearance={appearance}
      onFocusedOverflow={focusToggle}
    />
  ) : logoPosition === "center" ? (
    <span className="re-nav-bar-placeholder" />
  ) : null;
  const end = (
    <div className="re-nav-end">
      {actions ? (
        <HeaderActions
          actions={actions}
          bp={bp}
          icons={icons}
          onSearch={search}
        />
      ) : (
        legacyActions
      )}
      {schemeSwitch}
      <NavigationToggle
        ref={toggleRef}
        open={open}
        controls={panelId}
        variant={options.button}
        appearance={appearance}
        bp={bp}
        desktopHidden={options.hideButtonWhenEmpty && desktopEmpty && !open}
        mobileHidden={mobileEmpty && !open}
        reserveSpace={options.overflow}
        onClick={() => setOpen(!open)}
      />
    </div>
  );
  const content =
    layout === "rows" ? (
      <>
        <div className="re-nav-rows-top">
          {logo}
          {end}
        </div>
        {bar && (
          <div className="re-nav-rows-bar" data-bp={bp}>
            {bar}
          </div>
        )}
      </>
    ) : logoPosition === "center" ? (
      <>
        {bar}
        {logo}
        {end}
      </>
    ) : (
      <>
        {logo}
        {bar}
        {end}
      </>
    );

  const menu = (
    <NavigationFormatting
      website={navigation.websiteFormatting}
      menu={navigation.menuFormatting}
      references={navigation.colourReferences}
    >
      {rowClassName ? (
        <div
          className={rowClassName}
          style={rowStyle}
          data-logo={logoPosition}
          data-layout={layout}
          data-space-driven={options.overflow || undefined}
          data-menu-kind={presentation.kind}
        >
          {content}
        </div>
      ) : (
        content
      )}
      {probeHost &&
        createPortal(
          <div
            ref={probe}
            aria-hidden
            inert
            style={headerDesign}
            className={`${presentation.kind === "classic" ? "re-classic-menu" : presentation.kind === "floating" ? "re-drawer-nav" : (inheritedTextClassName ?? "")} re-nav-inheritance-probe`}
          >
            <a
              data-menu-inherited-text
              className={
                presentation.kind === "classic"
                  ? "re-classic-menu-row"
                  : undefined
              }
            >
              Menu
            </a>
          </div>,
          probeHost,
        )}
      <AnimatePresence>
        {open && (
          <NavigationPanel
            id={panelId}
            options={{ ...options, hoverEffect, hoverMarker }}
            desktopItems={desktopItems}
            mobileItems={mobileItems}
            bp={bp}
            anchor={anchor}
            toggle={toggleRef}
            onClose={close}
            logo={panelLogo ?? logo}
            actions={
              actions && (
                <MenuActions
                  actions={actions}
                  bp={bp}
                  icons={icons}
                  onSearch={search}
                  onNavigate={close}
                />
              )
            }
            extras={panelExtras}
            presentation={presentation}
            inheritedStyle={inheritedStyle}
          />
        )}
      </AnimatePresence>
    </NavigationFormatting>
  );
  return (
    <HeaderMotionProvider lookSystem={lookSystem}>{menu}</HeaderMotionProvider>
  );
}
